/**
 * @Author: Li, Yu Jia
 * @Date: 2019-12-02 13:36:31
 * @Last Modified by: Li, Yu Jia
 * @Last Modified time: 2021-04-07 10:38:23
 * @Usage: userimage.reducers.fun
 */

import {
  applyNoticeToggle,
  categoriesInit,
  clearSharedArtDict,
  recentArtsInit,
  removeSavedArtFromRecent,
  savedArtsCountInit,
  savedArtsListInit,
  sentimentListInit,
  setApplyNoticePrompt,
  setApplyNoticeScroll,
  setSavedArtThumbnail,
  setUserImageClone,
  setUserImageDimension,
  sharedArtsInit,
  sloganListInit,
  stockArtByCategory,
  verseListInit,
} from './actions';
import { changeElementApplyingStatus, updateRecentArt } from '../nextgen/actions';
import { setRecentArtDict, setSavedArtDict, setSharedArtDict, setStockArtDict } from '../canvas/actions';

import { createReducer } from '@reduxjs/toolkit';
import { userImageType } from './../utils/constants';

const sloganInitState = {};
const sloganDict = createReducer(sloganInitState, (builder) => {
  builder.addCase(sloganListInit, (state, action) => {
    const imprintAreaCode = action.payload.imprintAreaCode;
    state[imprintAreaCode] = action.payload.data;
  });
});

const verseInitState = [];
const verseList = createReducer(verseInitState, (builder) => {
  builder
    .addCase(verseListInit, (state, action) => {
      Object.assign(state, action.payload.data.verses);
    })
    .addCase(setUserImageDimension, (state, action) => {
      const userImage = action.payload.userImage;
      if (userImage.type === userImageType.Verse) {
        const dimension = action.payload.dimension;
        const findArt = state.find((x) => x.fileName === userImage.fileName);
        if (findArt) {
          Object.assign(findArt, { originalWidth: dimension.width, originalHeight: dimension.height });
        }
      }
    });
});

const sentimentInitState = [];
const sentimentList = createReducer(sentimentInitState, (builder) => {
  builder
    .addCase(sentimentListInit, (state, action) => {
      Object.assign(state, action.payload.data.sentiments);
    })
    .addCase(setUserImageDimension, (state, action) => {
      const userImage = action.payload.userImage;
      if (userImage.type === userImageType.Sentiment) {
        const dimension = action.payload.dimension;
        const findArt = state.find((x) => x.fileName === userImage.fileName);
        if (findArt) {
          Object.assign(findArt, { originalWidth: dimension.width, originalHeight: dimension.height });
        }
      }
    });
});

const stockArtCategoryInitState = {};
const stockArtCategoryDict = createReducer(stockArtCategoryInitState, (builder) => {
  builder.addCase(categoriesInit, (state, action) => {
    const { imprintAreaCode, categorylist } = action.payload;
    state[imprintAreaCode] = state[imprintAreaCode] || [];
    Object.assign(state[imprintAreaCode], categorylist);
  });
});

const stockArtDictInitState = {};
const stockArtDict = createReducer(stockArtDictInitState, (builder) => {
  builder
    .addCase(stockArtByCategory, (state, action) => {
      const { imprintAreaCode, categoryId, stockArtList } = action.payload;
      state[imprintAreaCode] = state[imprintAreaCode] || {};
      state[imprintAreaCode][categoryId] = state[imprintAreaCode][categoryId] || [];
      Object.assign(state[imprintAreaCode][categoryId], stockArtList);
    })
    .addCase(setUserImageDimension, (state, action) => {
      const userImage = action.payload.userImage;
      if (userImage.type === userImageType.StockArt) {
        const dimension = action.payload.dimension;
        Object.keys(state).map((cateid, _) => {
          const findArt = state[cateid].find((x) => x.stockId === userImage.stockId);
          if (findArt) {
            Object.assign(findArt, { originalWidth: dimension.width, originalHeight: dimension.height });
          }
          return true;
        });
      }
    })
    .addCase(setStockArtDict, (state, action) => {
      Object.assign(state, action.payload);
    });
});

const savedArtsDictInitState = {};
const savedArtDict = createReducer(savedArtsDictInitState, (builder) => {
  builder
    .addCase(savedArtsCountInit, (state, action) => {
      const processType = action.payload.processType;
      const imprintAreaCode = action.payload.imprintAreaCode;
      const key = `${imprintAreaCode}-${processType}`;
      state[key] = state[key] || { count: 0, list: {} };
      Object.assign(state[key], { count: action.payload.count });
    })
    .addCase(savedArtsListInit, (state, action) => {
      const list = action.payload.list;
      if (list.length > 0) {
        const processType = action.payload.processType;
        const imprintAreaCode = action.payload.imprintAreaCode;
        const key = `${imprintAreaCode}-${processType}`;
        state[key] = list;
      }
    })
    .addCase(setUserImageDimension, (state, action) => {
      const userImage = action.payload.userImage;
      if (userImage.type === userImageType.SavedArt) {
        const dimension = action.payload.dimension;
        Object.keys(state).map((key, _) => {
          if (state[key].list) {
            Object.keys(state[key].list).map((pg, _) => {
              const findArt = state[key].list[pg].find((x) => x.fileName === userImage.fileName);
              if (findArt) {
                //dimension:{originalWidth,originalHeight}
                Object.assign(findArt, { ...dimension });
              }
              return true;
            });
          }
          return true;
        });
      }
    })
    .addCase(setSavedArtThumbnail, (state, action) => {
      const { processType, imprintAreaCode, index, thumbnail, thumbnailWidth, thumbnailHeight } = action.payload;
      const key = `${imprintAreaCode}-${processType}`;
      if (state[key] && state[key][index]) {
        Object.assign(state[key][index], { thumbnail, thumbnailWidth, thumbnailHeight });
      }
    })

    .addCase(setSavedArtDict, (state, action) => {
      Object.assign(state, action.payload);
    })
    .addCase(changeElementApplyingStatus, (state, action) => {
      const { processType, imprintAreaCode, element, status } = action.payload;
      const key = `${imprintAreaCode}-${processType}`;
      if (state[key]) {
        const image = state[key].find((i) => i.description === element.description);
        if (image) {
          image.isApplying = status;
        }
      }
    });
});

const sharedArtInitState = {
  //"venderId-itemId-processType":[{}]
};
const sharedArtDict = createReducer(sharedArtInitState, (builder) => {
  builder
    .addCase(sharedArtsInit, (state, action) => {
      //console.log(action.payload);
      const list = action.payload.list;
      if (list.length > 0) {
        const currentImprintAreaKey = action.payload.currentImprintAreaKey;
        const sharedKey = currentImprintAreaKey;
        state[sharedKey] = state[sharedKey] || [];
        Object.assign(state[sharedKey], list);
      }
    })
    .addCase(setUserImageDimension, (state, action) => {
      const userImage = action.payload.userImage;
      if (userImage.type === userImageType.SharedArt) {
        const dimension = action.payload.dimension;
        Object.keys(state).map((key, _) => {
          const findArt = state[key].find((x) => x.fileName === userImage.fileName);
          if (findArt) {
            Object.assign(findArt, { originalWidth: dimension.width, originalHeight: dimension.height });
          }
          return true;
        });
      }
    })
    .addCase(
      clearSharedArtDict,
      (state) => (state = {}),
      [setSharedArtDict],
      (state, action) => {
        Object.assign(state, action.payload);
      }
    )
    .addCase(changeElementApplyingStatus, (state, action) => {
      const { imprintAreaKey, element, status } = action.payload;
      if (state[imprintAreaKey]) {
        const image = state[imprintAreaKey].find((i) => i.scene7FileName === element.scene7FileName);
        if (image) {
          image.isApplying = status;
        }
      }
    });
});

const recentArtDictInitState = {};
const recentArtDict = createReducer(recentArtDictInitState, (builder) => {
  builder
    .addCase(recentArtsInit, (state, action) => {
      const { imprintAreaCode, recent } = action.payload;
      state[imprintAreaCode] = state[imprintAreaCode] || [];
      let index = -1;
      //only for StockArt and Upload
      switch (recent.type) {
        case userImageType.StockArt: {
          index = state[imprintAreaCode].findIndex((x) => x.stockId === recent.stockId);
          break;
        }
        case userImageType.SavedArt:
        case userImageType.SharedArt: {
          index = state[imprintAreaCode].findIndex((x) => x.fileName === recent.fileName);
          break;
        }
        //userImageType.UploadLogo
        default: {
          index = state[imprintAreaCode].findIndex((x) => x.originFileName === recent.originFileName);
          break;
        }
      }
      if (index > -1) {
        state[imprintAreaCode].splice(index, 1);
      }
      state[imprintAreaCode].unshift(recent);
    })
    .addCase(updateRecentArt, (state, action) => {
      const { imprintAreaCode, recentArtGuid, image } = action.payload;
      const recentArt = state[imprintAreaCode].find((x) => x.recentArtGuid === recentArtGuid);
      if (recentArt) {
        if (image) recentArt.finalUpdated = image;
      }
    })
    .addCase(setUserImageDimension, (state, action) => {
      const userImage = action.payload.userImage;
      if (
        userImage.type === userImageType.StockArt ||
        userImage.type === userImageType.SavedArt ||
        userImage.type === userImageType.SharedArt
      ) {
        const dimension = action.payload.dimension;
        Object.keys(state).map((key, _) => {
          const findArt = state[key].find(
            (x) =>
              ((x.type === userImageType.StockArt && x.stockId === userImage.stockId) ||
                ((x.type === userImageType.SavedArt || x.type === userImageType.SharedArt) &&
                  x.fileName === userImage.fileName)) &&
              x.originalWidth === 'undefined' &&
              x.originalHeight === 'undefined'
          );
          if (findArt) {
            Object.assign(findArt, { originalWidth: dimension.width, originalHeight: dimension.height });
          }
          return true;
        });
      }
    })
    .addCase(setSavedArtThumbnail, (state, action) => {
      const { fileName, thumbnail, thumbnailWidth, thumbnailHeight } = action.payload;
      Object.keys(state).forEach((key, _) => {
        const findArt = state[key].find(
          (x) =>
            x.type === userImageType.SavedArt &&
            x.fileName === fileName &&
            x.thumbnail === undefined &&
            x.thumbnailWidth === undefined &&
            x.thumbnailHeight === undefined
        );
        if (findArt) {
          Object.assign(findArt, { thumbnail, thumbnailWidth, thumbnailHeight });
        }
      });
    })
    .addCase(removeSavedArtFromRecent, (state) => {
      //remove savedart from RecentArt List if user Sign Out
      Object.keys(state).forEach((imprintAreaCode) => {
        state[imprintAreaCode] = state[imprintAreaCode].filter((x) => x.type !== userImageType.SavedArt);
      });
    })

    .addCase(setRecentArtDict, (state, action) => {
      Object.assign(state, action.payload);
    })
    .addCase(changeElementApplyingStatus, (state, action) => {
      const { imprintAreaCode, elementIndex, status } = action.payload;
      if (state[imprintAreaCode] && state[imprintAreaCode][elementIndex]) {
        state[imprintAreaCode][elementIndex].isApplying = status;
      }
    });
});

const applyNoticeInitState = { status: false };
const applyNotice = createReducer(applyNoticeInitState, (builder) => {
  builder
    .addCase(applyNoticeToggle, (state, action) => {
      const { show, prompt, instance } = action.payload;
      state.status = show;
      if (action.payload.keepPromptText) return;

      state.instance = instance || undefined;
      state.scroll = show;

      if (!show || !prompt) {
        state.prompt = null;
      } else {
        state.prompt = prompt;
      }
    })
    .addCase(setApplyNoticePrompt, (state, action) => {
      state.prompt = action.payload.prompt;
    })
    .addCase(setApplyNoticeScroll, (state, action) => {
      state.scroll = action.payload.scroll;
    });
});

const userImageClone = createReducer(null, (builder) => {
  builder.addCase(setUserImageClone, (state, action) => action.payload);
});

const userImageReducer = {
  sloganDict,
  verseList,
  sentimentList,
  stockArtCategoryDict,
  stockArtDict,
  savedArtDict,
  sharedArtDict,
  recentArtDict,
  applyNotice,
  userImageClone,
};

export default userImageReducer;
